import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { useArticlePath } from "@/lib/i18n";
import { Section, stateColor, signed, deltaColor } from "./primitives";

const Row = ({ day, prev }: { day: any; prev: any }) => {
  const [open, setOpen] = useState(false);
  const toArticle = useArticlePath();
  const { data, isLoading } = useQuery({
    queryKey: ["layer-index-day", day.date],
    queryFn: async () => (await api.get(`/layer-index/day/${day.date}`)).data,
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const delta = prev ? day.value - prev.value : null;
  const top = (day.drivers || [])[0];
  const comps = day.components;
  return (
    <div className="border-b border-white/[0.05] last:border-0" data-testid={`li-forensic-${day.date}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid={`li-forensic-toggle-${day.date}`}
        className="w-full grid grid-cols-[92px_40px_52px_minmax(0,1fr)_16px] sm:grid-cols-[100px_44px_56px_130px_minmax(0,1fr)_16px] items-center gap-2 py-3 text-left group">
        <span className="nl-mono text-[11px] text-gray-500">{day.date}</span>
        <span className="nl-sg text-[15px] font-bold text-white">{day.value}</span>
        <span className="nl-mono text-[11px] font-bold" style={{ color: deltaColor(delta) }}>
          {delta == null ? "—" : delta === 0 ? "→ 0" : `${signed(delta)}`}
        </span>
        <span className="nl-mono text-[9.5px] font-bold uppercase tracking-[0.14em] truncate"
          style={{ color: stateColor(day.label) }}>
          {day.label}
        </span>
        <span className="hidden sm:block text-[12px] text-gray-500 truncate">
          {top ? `${top.dir === "up" ? "↑" : "↓"} ${top.label}` : ""}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-600 group-hover:text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 space-y-3">
          {comps && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 nl-mono text-[10.5px] text-gray-400"
              data-testid={`li-forensic-comps-${day.date}`}>
              <span>Sentiment <span className="text-white font-bold">{Math.round(comps.sentiment ?? 0)}</span></span>
              <span>Momentum <span className="text-white font-bold">{Math.round(comps.momentum ?? 0)}</span></span>
              <span>Stability <span className="text-white font-bold">{Math.round(comps.activity ?? 0)}</span></span>
              <span className="text-gray-600">
                {comps.bull_signals ?? 0} bull / {comps.bear_signals ?? 0} bear signals · {comps.stories_24h ?? 0} stories
              </span>
            </div>
          )}
          {(day.drivers || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {day.drivers.map((d: any) => (
                <span key={d.key} className={`nl-mono text-[9.5px] px-1.5 py-0.5 rounded border ${
                  d.dir === "up" ? "border-[#34D399]/25 text-[#34D399]" : "border-[#EF4444]/25 text-[#F87171]"}`}>
                  {d.dir === "up" ? "↑" : "↓"} {d.label}
                </span>
              ))}
            </div>
          )}
          {isLoading && <p className="text-xs text-gray-500">Loading…</p>}
          {!isLoading && data?.headlines?.length ? (
            <ul className="space-y-1.5">
              {data.headlines.map((h: any, i: number) => (
                <li key={h.slug || i} className="flex gap-2 items-start">
                  <span aria-hidden className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                    h.direction === "bull" ? "bg-[#22C55E]" : h.direction === "bear" ? "bg-[#DC2626]" : "bg-gray-500"}`} />
                  <Link to={toArticle(h.slug)}
                    className="font-news text-[13px] text-gray-300 hover:text-[#00E5FF] transition-colors leading-snug">
                    {h.title}
                  </Link>
                </li>
              ))}
            </ul>
          ) : !isLoading && open ? (
            <p className="text-xs text-gray-600">No mood-driving headlines recorded for this day.</p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const ForensicHistory = ({ history }: { history: any[] }) => {
  const desc = [...history].reverse();
  return (
    <Section num="08" title="Daily History & Drivers" sub="Forensic archive" testid="li-forensic-section">
      <div className="hidden sm:grid grid-cols-[100px_44px_56px_130px_minmax(0,1fr)_16px] gap-2 pb-2 border-b border-white/[0.08] nl-mono text-[9px] uppercase tracking-[0.2em] text-gray-600">
        <span>Date</span><span>Score</span><span>Δ</span><span>State</span><span>Top driver</span><span />
      </div>
      <div>
        {desc.map((d, i) => <Row key={d.date} day={d} prev={desc[i + 1] || null} />)}
        {!desc.length && <p className="text-sm text-gray-500 pt-4">No history yet.</p>}
      </div>
    </Section>
  );
};
